import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useIntl } from "react-intl";

import useSites, { EditSite, useDeleteSite, useSaveSite } from "../../hooks/useSites";
import { useDebounce } from "../../hooks/useDebounce";
import ManageSite from "../../components/sites/ManageSite";
import AssignIdsToSite from "../../components/sites/AssignIdsToSite";
import Button from "../../components/ui/button";
import { displayDate } from "../../utils/date";
import { DateDataBaseType } from "../../types/database";
import { SiteSummary } from "../../types/user";
import { RequestError } from "../../errors/RequestError";
import useAuth from "../../hooks/useAuth";

import CroixIcon from "../../components/icons/Close";
import LinkIcon from "../../components/icons/Link";
import SettingIcon from "../../components/icons/Setting";
import EditIcon from "../../components/icons/Edit";
import DeleteIcon from "../../components/icons/Delete";
import SearchIcon from "../../components/icons/Search";
import PageSectionHeader from "../../components/PageSectionHeader";

type SiteRow = SiteSummary & DateDataBaseType;

export default function Sites() {
  const intl = useIntl();
  const { user: currentUser } = useAuth();
  const { fetchSites, sites, isLoading } = useSites();
  const { saveSite, saved, error: errorWhenSaving } = useSaveSite();
  const { deleteSite, response: deleted, error: errorWhenDeleting } = useDeleteSite();

  const [showModal, setShowModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [siteToEdit, setSiteToEdit] = useState<SiteRow | null>(null);
  const [assignSite, setAssignSite] = useState<SiteRow | null>(null);
  const [error, setError] = useState<Error | RequestError | null>(null);
  const [success, setSuccess] = useState<{ message: string } | null>(null);
  const [searchInput, setSearchInput] = useState<string>();
  const termToSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    setError(errorWhenSaving ?? errorWhenDeleting ?? null);
  }, [errorWhenSaving, errorWhenDeleting]);

  useEffect(() => {
    if (deleted) {
      fetchSites();
      closeModal();
      setSuccess({ message: intl.formatMessage({ id: "sites.deletedSuccess" }) });
      setTimeout(() => setSuccess(null), 3000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted, fetchSites, intl]);

  useEffect(() => {
    if (saved) {
      fetchSites();
      setSuccess({
        message: siteToEdit
          ? intl.formatMessage({ id: "sites.updatedSuccess" })
          : intl.formatMessage({ id: "sites.createdSuccess" })
      });
      if (!siteToEdit) {
        setSiteToEdit(saved.data as SiteRow);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSites, saved, intl]);

  const filteredSites = useMemo(() => {
    if (!sites) return null;
    const term = termToSearch?.trim().toLowerCase();
    if (!term) return sites as SiteRow[];
    return (sites as SiteRow[]).filter((site) => site.name.toLowerCase().includes(term));
  }, [sites, termToSearch]);

  function onSiteEdit(site: SiteRow): void {
    setShowModal(true);
    setSiteToEdit(site);
  }

  function closeModal() {
    setShowModal(false);
    setSiteToEdit(null);
    setDeleteModal(false);
    setError(null);
    setSuccess(null);
  }

  const onSave = useCallback((action: "ADD" | "UPDATE", site: EditSite) => {
    if (action === "UPDATE" && siteToEdit) {
      saveSite(action, site, siteToEdit.id);
    } else {
      saveSite(action, site);
    }
  }, [saveSite, siteToEdit]);

  const onDelete = useCallback(() => {
    setDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (siteToEdit?.id) {
      deleteSite(siteToEdit.id);
      setDeleteModal(false);
    }
  }, [deleteSite, siteToEdit]);

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(
            { id: "sites.documentTitle" },
            { appName: import.meta.env.VITE_APP_NAME }
          )}
        </title>
      </Helmet>

      <div className="h-full flex flex-col">
        {(success || error) && (
          <div className={`mb-4 p-4 rounded-lg ${success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className={`text-sm font-medium ${success ? "text-green-800" : "text-red-800"}`}>
              {success
                ? success.message
                : error?.message || intl.formatMessage({ id: "common.errorGeneric" })}
            </p>
          </div>
        )}

        <PageSectionHeader>
          <h2 className="text-2xl font-semibold" style={{ color: "#023865" }}>
            {intl.formatMessage(
              { id: "common.welcomeUser" },
              { firstName: currentUser?.firstName, lastName: currentUser?.lastName }
            )}
          </h2>
        </PageSectionHeader>

        <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="relative flex-1 max-w-md">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="search"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder={intl.formatMessage({ id: "sites.searchPlaceholder" })}
              value={searchInput || ""}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <button
            className="px-4 py-3 flex items-center gap-2 text-white rounded-lg shrink-0 whitespace-nowrap"
            type="button"
            style={{ backgroundColor: "#E94E1B" }}
            onClick={() => setShowModal(true)}
          >
            <span className="text-xl">+</span>
            {intl.formatMessage({ id: "sites.createSite" })}
          </button>
        </div>

        <section className="bg-white rounded-2xl p-6" style={{ border: "1px solid #E4E7EC" }}>
          <div className="mb-6 flex justify-between items-center">
            <div>
              <SettingIcon className="w-6 h-6 text-gray-700 mb-2" />
              <h3 className="text-lg font-semibold text-gray-900">
                {intl.formatMessage({ id: "sites.listTitle" })}
              </h3>
            </div>
            <span className="text-sm text-gray-600">
              {intl.formatMessage(
                { id: "sites.siteCount" },
                { count: filteredSites?.length ?? 0 }
              )}
            </span>
          </div>

          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                <p className="mt-2 text-gray-500">{intl.formatMessage({ id: "common.loading" })}</p>
              </div>
            ) : filteredSites && filteredSites.length > 0 ? (
              <table className="w-full border-separate border-spacing-y-3">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: "45%" }}>
                      {intl.formatMessage({ id: "sites.colName" })}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: "25%" }}>
                      {intl.formatMessage({ id: "sites.colCreated" })}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: "30%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSites.map((site) => (
                    <tr key={site.id}>
                      <td
                        className="px-4 py-4 text-left text-sm font-medium text-gray-900 rounded-l-2xl"
                        style={{ border: "1px solid #E4E7EC", borderRight: "none" }}
                      >
                        {site.name}
                      </td>
                      <td
                        className="px-4 py-4 text-left text-sm"
                        style={{ borderTop: "1px solid #E4E7EC", borderBottom: "1px solid #E4E7EC" }}
                      >
                        <span
                          className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium"
                          style={{ backgroundColor: "#F9FAFB", color: "#9CA3AF" }}
                        >
                          {site.createdAt
                            ? displayDate(site.createdAt)
                            : intl.formatMessage({ id: "common.dash" })}
                        </span>
                      </td>
                      <td
                        className="px-4 py-4 text-left rounded-r-2xl"
                        style={{ border: "1px solid #E4E7EC", borderLeft: "none" }}
                      >
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap"
                            style={{ color: "#023865", backgroundColor: "#F3F6F9" }}
                            onClick={() => setAssignSite(site)}
                          >
                            <LinkIcon className="w-4 h-4" />
                            {intl.formatMessage({ id: "sites.assignIds.button" })}
                          </button>
                          <button
                            className="transition-colors"
                            style={{ color: "#023865" }}
                            onClick={() => onSiteEdit(site)}
                            title={intl.formatMessage({ id: "common.edit" })}
                          >
                            <EditIcon className="w-5 h-5" />
                          </button>
                          <button
                            className="transition-colors"
                            style={{ color: "#E94E1B" }}
                            onClick={() => {
                              setSiteToEdit(site);
                              setDeleteModal(true);
                            }}
                            title={intl.formatMessage({ id: "common.delete" })}
                          >
                            <DeleteIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {intl.formatMessage({ id: "sites.empty" })}
              </div>
            )}
          </div>
        </section>
      </div>

      <div
        className={`py-5 md:py-10 fixed top-0 right-0 bottom-0 w-full h-full grid place-items-center transition-colors z-50
        ${showModal ? "bg-black/35 pointer-events-auto overflow-y-auto" : "pointer-events-none"}`}
        data-overlay
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick={(e: any) => e.target.dataset?.overlay && closeModal()}
      >
        <div
          className={`md:ml-auto pt-10 pb-10 px-8 md:w-1/2 lg:w-1/3 2xl:w-1/4 bg-white rounded-lg relative shadow-xl
          transition-transform ${showModal ? "mx-5 md:mr-8 lg:mr-10 translate-x-0" : "translate-x-full"}`}
        >
          <button onClick={() => closeModal()} className="p-1.5 absolute top-5 right-5">
            <CroixIcon />
          </button>
          <ManageSite
            key={siteToEdit?.id ?? 0}
            error={error}
            success={success}
            site={siteToEdit}
            onSave={onSave}
            onDelete={onDelete}
          />
        </div>
      </div>

      <div
        className={`py-5 md:py-10 fixed top-0 right-0 bottom-0 w-full h-full grid place-items-center transition-colors z-50
        ${assignSite ? "bg-black/35 pointer-events-auto overflow-y-auto" : "pointer-events-none"}`}
        data-overlay
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick={(e: any) => e.target.dataset?.overlay && setAssignSite(null)}
      >
        <div
          className={`md:ml-auto pt-10 pb-10 px-8 md:w-1/2 lg:w-2/5 bg-white rounded-lg relative shadow-xl
          transition-transform ${assignSite ? "mx-5 md:mr-8 lg:mr-10 translate-x-0" : "translate-x-full"}`}
        >
          <button onClick={() => setAssignSite(null)} className="p-1.5 absolute top-5 right-5">
            <CroixIcon />
          </button>
          {assignSite && <AssignIdsToSite key={assignSite.id} site={assignSite} />}
        </div>
      </div>

      {deleteModal && (
        <div className="fixed top-0 right-0 w-full h-full grid place-items-center bg-black/35 z-50">
          <div className="px-8 py-6 w-3/4 md:w-2/4 lg:w-2/5 2xl:w-1/4 rounded-lg bg-white shadow-2xl">
            <h3 className="text-center text-gray-900 font-semibold text-xl">
              {intl.formatMessage({ id: "sites.deleteConfirmTitle" })} <br />
              <strong className="font-bold">{siteToEdit?.name}</strong>
            </h3>
            <p className="text-sm font-medium mt-3 text-center text-gray-600">
              {intl.formatMessage({ id: "sites.deleteConfirmBody" })}
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              <Button theme="black" size="small" onClick={() => setDeleteModal(false)}>
                {intl.formatMessage({ id: "common.cancel" })}
              </Button>
              <Button theme="danger" size="small" onClick={confirmDelete}>
                {intl.formatMessage({ id: "common.delete" })}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
